import { assert } from "@ember/debug";
import dayjs from "dayjs";
import type { IntlService } from "ember-intl";
import type ConsultationPlannedJournalEvent from "./consultation-planned-journal-event";
import type TreatmentHistoryChangedJournalEvent from "./treatment-history-changed-journal-event";

const CureDatePattern = /^cure\d*Date$/;
const PeriodDateNames = ["startDate", "endDate"];

type TreatmentProtocolType = "cure" | "period" | "surgery";

export type TreatmentProtocol =
  // Multiple
  | "raac-gynecological-surgery"
  // Breast
  | "abemaciclib-verzenios"
  | "ribociclib-kisqali"
  // Lung
  | "atezolizumab"
  | "pembrozimulab"
  | "carboplatine-alimta-pembrozimulab"
  // Pancreatic
  | "olaparib-initial"
  | "olaparib-following"
  | "regorafenib-stivarga-initial"
  | "regorafenib-stivarga-renewal"
  // Prostate
  | "prostatectomy-total"
  | "adt-anti-androgen"
  | "adt-chemo"
  // Default
  | "default-chemotherapy"
  | "default-radiotherapy"
  | "default-immunotherapy"
  | "default-other-follow-up-events"
  | "default-targeted-therapy"
  | "default-surgery"
  | "default-hormonotherapy";

/**
 * A Protocol can be a saved protocol created from the TreatmentHistoryChangedJournalEvent and having an uuid
 * But a Protocol can be created from a changeset and not be saved to the TreatmentHistoryChanged yet and not having a uuid
 */
export default class Protocol {
  endDate?: Date;
  paused?: boolean;
  declare protocol: TreatmentProtocol;
  startDate?: Date;
  surgery?: Date;
  type?: string;
  types?: string[];
  uuid?: string;

  get canBePaused(): boolean {
    return this.isActive && !this.isPaused && this.allDatesFilled;
  }

  get cureDates(): Date[] {
    return this.cureDateValues.filter(d => Boolean(d)) as Date[];
  }

  /**
   * Active means that there is a cure in the future
   */
  get isActive(): boolean {
    switch (this.treatmentProtocolType) {
      case "period":
        // startDate or endDate in the future
        // or startDate in the past but no endDate
        return (
          PeriodDateNames.some(n => dayjs().isBefore(this[n])) ||
          Boolean(this.startDate && !this.endDate)
        );
      case "surgery":
        return Boolean(this.surgery || this["cureDate"]);
      default:
        return Boolean(this.nextCureDate);
    }
  }

  get isDefaultProtocol(): boolean {
    return this.protocol.includes("default");
  }

  get isFinished(): boolean {
    return this.allDatesFilled && !this.isActive;
  }

  get isPaused(): boolean {
    return (
      (Boolean(this.uuid) && this.cureDateValues.some(d => d === null)) ||
      Boolean(this.paused)
    );
  }

  get lastCureDate(): Date | undefined {
    return this.lastCureIndex
      ? this.getCureDate(this.lastCureIndex)
      : undefined;
  }

  get lastCureIndex(): number | undefined {
    const today = dayjs();
    let nextCureIndex;
    let nearestCureDiff = -Infinity;

    this.cureDates.forEach((cureDate, index) => {
      const diff = dayjs(cureDate).diff(today);
      if (diff < 0 && diff > nearestCureDiff) {
        nextCureIndex = index + 1;
        nearestCureDiff = diff;
      }
    });

    return nextCureIndex;
  }

  get lastCureKey(): string | undefined {
    return this.lastCureIndex
      ? Protocol.getCureDateId(this.lastCureIndex)
      : undefined;
  }

  /**
   * Return the next important date not depending on the protocol type
   * Could be in the past for surgeries
   */
  get nextActiveOrImportantDate(): Date | undefined {
    switch (this.treatmentProtocolType) {
      case "period":
        return dayjs().isAfter(this.startProtocolDate)
          ? this.endDate
          : this.startProtocolDate;
      case "surgery":
        return this.surgery || this["cureDate"];
      default:
        return this.nextCureDate;
    }
  }

  get nextCureDate(): Date | undefined {
    return this.nextCureIndex
      ? this.getCureDate(this.nextCureIndex)
      : undefined;
  }

  get nextCureIndex(): number | undefined {
    const today = dayjs();
    let nextCureIndex: number | undefined;
    let nearestCureDiff = Infinity;

    this.cureDates.forEach((cureDate, index) => {
      const diff = dayjs(cureDate).diff(today);
      if (diff > 0 && diff < nearestCureDiff) {
        nextCureIndex = index + 1;
        nearestCureDiff = diff;
      }
    });

    return nextCureIndex;
  }

  get nextCureKey(): string | undefined {
    return this.nextCureIndex
      ? Protocol.getCureDateId(this.nextCureIndex)
      : undefined;
  }

  get startProtocolDate(): Date {
    return this[Protocol.getCureDateId(1)] || this.startDate;
  }

  get treatmentProtocolType(): TreatmentProtocolType {
    if (this.surgery || this["cureDate"]) return "surgery";
    else if (PeriodDateNames.some(n => this[n])) return "period";
    return "cure";
  }

  private get allDatesFilled(): boolean {
    return this.cureDates.length == this.numberOfCures;
  }

  /**
   * It returns every cureDate values even the ones set to null
   * It could be usefull for paused treatment
   */
  private get cureDateValues(): (Date | null)[] {
    return Object.entries(this)
      .map(([key, value]) => (CureDatePattern.test(key) ? value : undefined))
      .filter(d => d !== undefined);
  }

  private get numberOfCures(): number {
    return this.cureDateValues.length;
  }

  constructor(args: object) {
    assert(
      "A protocol must have a protocol property",
      Boolean(args["protocol"])
    );
    Object.assign(this, args);
  }

  static getCureDateId(index: number): string {
    return `cure${index}Date`;
  }

  static new(
    args: { protocol?: TreatmentProtocol } & { [key: string]: any }
  ): Protocol {
    switch (args.protocol) {
      case "abemaciclib-verzenios":
      case "ribociclib-kisqali": {
        const startDate = args["treatment-start-date"];
        const endDate = dayjs(startDate).add(7, "months");
        return new Protocol({ ...args, startDate, endDate });
      }
      default:
        return new Protocol(args);
    }
  }

  getConsultations(
    treatmentHistory: TreatmentHistoryChangedJournalEvent
  ): ConsultationPlannedJournalEvent[] {
    if (this.uuid) return [];
    return treatmentHistory.consultationPlannedJournalEvents.filter(consult =>
      consult.originConsultationIdentifier.includes(this.uuid!)
    );
  }

  getProtocolLabel(intl: IntlService): string {
    if (this.protocol == "default-other-follow-up-events" && this.type) {
      return intl.t(`other-events.${this.type}`);
    }
    return intl.t(`treatment-protocol.${this.protocol}`);
  }

  private getCureDate(index: number): Date {
    return this[Protocol.getCureDateId(index)];
  }
}
